import React, { useState } from 'react';
import { 
  Box,
  Button,
  Typography,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import ReceiptIcon from '@mui/icons-material/Receipt';
import NoteIcon from '@mui/icons-material/Note';
import ChatWidget from '../components/ChatWidget';
import proxyClient from '../utils/proxyClient'; // Import proxyClient at the top of the file
// import supabase from '../utils/supabaseClient'; // Remove Supabase client

// Helper function to parse spreadsheet data
const parseSpreadsheetData = (text: string): { [key: string]: string } => {
  const data: { [key: string]: string } = {};
  const lines = text.split('\n');
  // Updated regex to handle optional curly braces {} around the cell reference
  const regexWithQuotes = /^{?([A-Z]+\d+)}?:\s*'(.*)'$/;
  const regexWithoutQuotes = /^{?([A-Z]+\d+)}?:\s*(.+)$/;

  lines.forEach(line => {
    let match = line.match(regexWithQuotes); // Try matching format with quotes first
    if (match) {
      const cell = match[1]; // Captured cell identifier (e.g., C1, A12)
      const value = match[2].trim();
      if (value) { 
        data[cell] = value;
      }
    } else {
      match = line.match(regexWithoutQuotes); // Try matching format without quotes
      if (match) {
        const cell = match[1];
        const value = match[2].trim();
        if (value) {
          data[cell] = value;
        }
      }
    }
  });

  console.log("Parsed Data:", data); // For debugging
  return data;
};

// Function to call OpenAI API via backend
const callOpenAI = async (prompt: string): Promise<string> => {
  try {
    // Use proxyClient instead of direct fetch
    const { data, error } = await proxyClient.post('/api/borrower-data/openai', {
      prompt: prompt,
      type: 'business_notes' // Specify the type for better context
    });

    if (error) {
      throw new Error('Failed to generate content: ' + error);
    }

    return data.text || 'No response generated';
  } catch (error) {
    console.error('Error calling OpenAI API:', error);
    return `Failed to generate content. Please try again later.`;
  }
};

// Placeholder functions for saving business notes
const saveBusinessNotes = async (dealNumber: string, section: string, content: string): Promise<boolean> => {
  try {
    // Get authorization token
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication token not found');
    }

    // Call backend endpoint
    const response = await fetch('/api/borrower-data/business-notes', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        dealNumber,
        section,
        content
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to save business notes: ${response.statusText}`);
    }

    return true;
  } catch (error) {
    console.error('Error saving business notes:', error);
    return false;
  }
};

const BusinessNotesPage: React.FC = () => {
  const [noteText, setNoteText] = useState('');
  const [parsedData, setParsedData] = useState<{ [key: string]: string } | null>(null);
  const [businessInfoContent, setBusinessInfoContent] = useState<React.ReactNode>(null);
  const [documentsContent, setDocumentsContent] = useState<React.ReactNode>(null);
  const [expensesContent, setExpensesContent] = useState<React.ReactNode>(null);
  const [finalNotesContent, setFinalNotesContent] = useState<React.ReactNode>(null);
  const [isLoading, setIsLoading] = useState(false); // To show loading state
  
  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNoteText(e.target.value);
  };
  
  // --- Generation Functions ---

  const generateBusinessInfo = async (data: { [key: string]: string }): Promise<React.ReactNode> => {
    const C1 = data['C1'] || '[Owner Name]';
    const C2 = data['C2'] || '[Business Name]';
    const C3 = data['C3'] || '[Years Operating]';
    const C4 = data['C4'] || '[Years in Industry]';
    const C5 = data['C5'] || '[Ownership %]';
    const C6 = data['C6'] || 'No';
    const E6 = data['E6'] || '[Website]';
    const C7 = data['C7'] || 'No';
    const E7 = data['E7'] || '[# Employees]';
    const C8 = data['C8'] || 'No';
    const E8 = data['E8'] || '[# Subcontractors]';
    const C9 = data['C9'] || '[Business Classification]';
    const C11 = data['C11'] || '[Operating Name]';

    let businessDescPrompt = `Write a description easily readable & fluent in a paragraph that is not for marketing but for an auditor to understand what kind of business it is, how they operate, where they are located, and how they get customers. Get info from the below: Business name: ${C11}.Business description: ${C2}.`;
    if (C6 === 'Yes' && E6 !== '[Website]') {
      businessDescPrompt += ` Add the website ${E6}.`;
    }
    businessDescPrompt += ` Begin with the value from Business name.`;
    const businessDesc = await callOpenAI(businessDescPrompt);

    let operationsDesc = 'operates by himself';
    if (C7 === 'No' && C8 === 'Yes') {
      operationsDesc = `operates by himself and uses ${E8} subcontractors especially on busier times`;
    } else if (C7 === 'Yes' && C8 === 'No') {
      operationsDesc = `operates by himself and ${E7} employees`;
    } else if (C7 === 'Yes' && C8 === 'Yes') {
      operationsDesc = `operates by himself, ${E7} employees, and ${E8} subcontractors`;
    }
    const operationsPrompt = `In a separate paragraph, describe in a readable and fluent but condensed manner how the company now operates: ${operationsDesc}. Explain why this is reasonable for this type of business.`;
    const operationsExplanation = await callOpenAI(operationsPrompt);
    
    const classificationPrompt = `Explain In a readable and fluent but condensed manner,  why the business "${C11}" is considered as '${C9}'.`;
    const classificationExplanation = await callOpenAI(classificationPrompt);

    return (
      <Box>
        <Typography variant="body1" paragraph><strong>BFS NOTES | BUSINESS DESCRIPTION:</strong></Typography>
        <Typography variant="body1" paragraph>
          "{C1}" owns and operates "{C11}". The business has been operating for "{C3}" years. The client has been in the industry for "{C4}" years and owns "{C5}%" of the business.
        </Typography>
        <Typography variant="body1" paragraph>{businessDesc}</Typography>
        <Typography variant="body1" paragraph><strong>Website:</strong> {C6 === 'Yes' ? E6 : 'N/A'}</Typography>
        <Typography variant="body1" paragraph>{operationsExplanation}</Typography>
        <Typography variant="body1" paragraph><strong>The business is:</strong> {C9}</Typography>
        <Typography variant="body1" paragraph>{classificationExplanation}</Typography>
      </Box>
    );
  };

  const generateDocuments = async (data: { [key: string]: string }): Promise<React.ReactNode> => {
    const C10 = data['C10'] || '[Confirmation Doc]';
    const E10 = data['E10'] || '[Doc Entity]';
    const C11 = data['C11'] || '[Operating Name]';
    const C12 = data['C12'] || '[Date Confirmed]';
    const C13 = data['C13'] || '[Registered Owner]';
    const C28 = data['C28'] || '[Stated Income]';
    const C14 = data['C14'] || 'No';
    const E14 = data['E14'] || '[HST/GST #]';
    const G14 = data['G14'] || '[HST/GST Reason]';
    const C15 = data['C15'] || '[Start Month]';
    const D15 = data['D15'] || '[End Month]';
    const C16 = data['C16'] || '[Bank Account Type]';
    const C17 = data['C17'] || '[Invoice Support Type]';
    const C18 = data['C18'] || '[Gross Annual Income]';
    const E18 = data['E18'] || '[Income Method]';
    const E19 = data['E19'] || 'No'; // Wire transfers Yes/No
    const C20 = data['C20'] || '';
    const C21 = data['C21'] || '';
    const C22 = data['C22'] || '';
    const C23 = data['C23'] || '';

    let incomeReasonablenessPrompt = `Explain in a readable, fluent and condensed manner why it's reasonable for this business to receive income via: '${E18}'`;
    if (E19 === 'Yes') {
      incomeReasonablenessPrompt += ' and wire transfers';
    }
    incomeReasonablenessPrompt += `. Begin with: 'Reasonable to receive..'`;
    const incomeReasonablenessExplanation = await callOpenAI(incomeReasonablenessPrompt);

    const paymentVariabilityExplanation = await callOpenAI("Explain in readable , fluent but condensed manner why this business has variable payments. Begin with: 'Payments vary...'");

    const invoiceExplanations = [C20, C21, C22, C23].filter(item => item);

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom><strong>Confirmation of Ownership / Tenure</strong></Typography>
        <Typography variant="body2">"{C10}" on file for: "{E10}"</Typography>
        <Typography variant="body2">Business operating as: "{C11}"</Typography>
        <Typography variant="body2">Confirmed date of operation: "{C12}"</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>Registered Owner: "{C13}"</Typography>

        <Typography variant="subtitle2" gutterBottom><strong>SDL (Self-Declared Letter)</strong></Typography>
        <Typography variant="body2">Stated income letter signed for: ${C28}</Typography>
        <Typography variant="body2">HST/GST #: {C14 === 'Yes' ? `${E14}, active on the Government of Canada online registry.` : `N/A, ${G14}`}</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>The customer has signed and dated the self-declared letter to confirm that they have filed their income taxes current and do not owe any monies to the CRA.</Typography>

        <Typography variant="subtitle2" gutterBottom><strong>Bank Statements and Invoices</strong></Typography>
        <Typography variant="body2">Bank statements on file from: {C15} to {D15}</Typography>
        <Typography variant="body2">Bank account: {C16}</Typography>
        <Typography variant="body2">Invoices on file supporting the {C17}</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>Invoices are matched with the months used to calculate income to confirm at least 2 deposits.</Typography>

        <Typography variant="subtitle2" gutterBottom><strong>Gross Income</strong></Typography>
        <Typography variant="body2">Gross annualized income: ${C18}, Income derived from only these months: from {C15} to {D15}, Number of months considered: #12</Typography>
		<Typography variant="body2">Gross income was calculated using: {E18}</Typography>
        <Typography variant="body2" paragraph>{incomeReasonablenessExplanation}</Typography>
        <Typography variant="body2" paragraph>{paymentVariabilityExplanation}</Typography>
        <Typography variant="body2">Wire-Transfers: {E19}</Typography>


        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}><strong>Invoice Explanation</strong></Typography>
        {invoiceExplanations.length > 0 ? (
          invoiceExplanations.map((item, index) => (
            <Typography key={index} variant="body2" paragraph>{item}</Typography>
          ))
        ) : (
          <Typography variant="body2" paragraph>N/A</Typography>
        )}
      </Box>
    );
  };

  const generateExpenses = async (data: { [key: string]: string }): Promise<React.ReactNode> => {
    const E25 = data['E25'] || '[Excluded Withdrawals]';
    const E26 = data['E26'] || '[Non-Applicable Expenses]';

    // Define pairs of Expense Name cells (A column) and Amount cells (B column)
    const expenseCellPairs = [
        ['A12', 'B12'], ['A13', 'B13'], ['A14', 'B14'], ['A15', 'B15'], ['A16', 'B16'], 
        ['A17', 'B17'], ['A18', 'B18'], ['A19', 'B19'], ['A20', 'B20'],
        ['A24', 'B24'], ['A25', 'B25'], ['A26', 'B26'], ['A27', 'B27'], ['A28', 'B28'], 
        ['A29', 'B29'], ['A30', 'B30'], ['A31', 'B31'], ['A32', 'B32'], ['A33', 'B33'], 
        ['A34', 'B34'], ['A35', 'B35']
    ];

    // Filter and format the expenses
    const formattedExpenses = expenseCellPairs
        .map(([nameCell, amountCell]) => ({
            name: data[nameCell] || `[${nameCell}]`, // Use cell ref as fallback name
            amount: data[amountCell]
        }))
        .filter(item => item.amount); // Keep only items where amount exists

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom><strong>Expenses:</strong></Typography>
        {formattedExpenses.length > 0 ? (
          <List dense sx={{ mb: 2 }}>
            {formattedExpenses.map((item, index) => (
              <ListItem key={index} sx={{ pl: 2, py: 0 }}> 
                <ListItemText primary={`${item.name}: $${item.amount}`} />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" sx={{ mb: 2 }}>No specific monthly expenses listed.</Typography>
        )}

        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}><strong>Standard Notes:</strong></Typography>
        <Typography variant="body2" paragraph>
          - Expenses used on the spreadsheet that are not showing in the bank statements were provided by: broker and adjusted as per industry standards and bank statement figures.<br />
          - The expenses on the spreadsheet are in line with the invoices on file.<br />
          - The expenses on the spreadsheet are in line with the nature of the business and make sense for the gross income level.<br />
          - All recurring business expenses in the bank statements are included on the spreadsheet.
        </Typography>

        <Typography variant="body2" paragraph><strong>Bank Account Withdrawals Not Included as Expenses:</strong> '{E25}'</Typography>
        <Typography variant="body2" paragraph><strong>The following expenses do not apply to this business:</strong> '{E26}'</Typography>
      </Box>
    );
  };

  const generateFinalNotes = async (data: { [key: string]: string }): Promise<React.ReactNode> => {
    const C27 = data['C27'] || '[Net Income]';
    const E27 = data['E27'] || '[Qualifying Amount]';
    const G27 = data['G27'] || '[Basis for Qualifying]';
    const E28 = data['E28'] || '[Stated Income]';
    const G28 = data['G28'] || '[Stated Income Basis]';

    // Generate the stated income line using AI
    const statedIncomePrompt = `Write the below in a more readable and fluent yet condenced manner : Stated income: ${E28}, ${G28}`;
    const formattedStatedIncome = await callOpenAI(statedIncomePrompt);

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom><strong>NET INCOME</strong></Typography>
        <Typography variant="body2">After all expenses, calculated net income is: ${C27} per year</Typography>
        <Typography variant="body2">Annual amount used to qualify the deal: ${E27} based on {G27}</Typography>
        <Typography variant="body2">{formattedStatedIncome}</Typography>

        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}><strong>OTHER NOTES</strong></Typography>
        <Typography variant="body2">Spreadsheet uploaded to docs log.</Typography>
      </Box>
    );
  };

  // --- End Generation Functions ---

  const handleSubmit = async () => {
    setIsLoading(true);
    setBusinessInfoContent(null);
    setDocumentsContent(null);
    setExpensesContent(null);
    setFinalNotesContent(null);
    setParsedData(null);
    
    try {
      const parsed = parseSpreadsheetData(noteText);
      setParsedData(parsed);

      // Generate content for all sections
      const [bizInfo, docs, expenses, finalNotes] = await Promise.all([
        generateBusinessInfo(parsed),
        generateDocuments(parsed),
        generateExpenses(parsed),
        generateFinalNotes(parsed)
      ]);

      setBusinessInfoContent(bizInfo);
      setDocumentsContent(docs);
      setExpensesContent(expenses);
      setFinalNotesContent(finalNotes);

    } catch (error) {
      console.error("Error processing notes:", error);
      // Optionally show an error message to the user
      alert('Error processing notes. Check console for details.');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Box sx={{ p: 3, maxWidth: '900px', mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Business Notes
      </Typography>
      
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <TextField
          label="Paste business information here"
          multiline
          fullWidth
          rows={6}
          value={noteText}
          onChange={handleTextChange}
          variant="outlined"
          placeholder="Paste or type business information that needs to be processed..."
          sx={{ mb: 2 }}
        />
        
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSubmit}
          size="large"
          disabled={isLoading}
          sx={{ minWidth: '120px' }}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
        </Button>
      </Paper>
      
      {/* Collapsible Sections - Show only after submission */}
      {businessInfoContent && documentsContent && expensesContent && finalNotesContent && (
        <Box>
          <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
            Generated Business Notes
          </Typography>
          
          {/* Section 1: Business Info */}
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="business-info-content"
              id="business-info-header"
              sx={{ backgroundColor: 'rgba(0, 0, 0, 0.03)' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BusinessIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle1">Business Info/Seasonality</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {businessInfoContent}
            </AccordionDetails>
          </Accordion>
          
          {/* Section 2: Documents */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="documents-content"
              id="documents-header"
              sx={{ backgroundColor: 'rgba(0, 0, 0, 0.03)' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <DescriptionIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle1">Documents</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {documentsContent}
            </AccordionDetails>
          </Accordion>
          
          {/* Section 3: Expenses */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="expenses-content"
              id="expenses-header"
              sx={{ backgroundColor: 'rgba(0, 0, 0, 0.03)' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ReceiptIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle1">Expenses</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {expensesContent}
            </AccordionDetails>
          </Accordion>
          
          {/* Section 4: Final Notes */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="final-notes-content"
              id="final-notes-header"
              sx={{ backgroundColor: 'rgba(0, 0, 0, 0.03)' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <NoteIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle1">Final Notes</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {finalNotesContent}
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
      <ChatWidget />
    </Box>
  );
};

export default BusinessNotesPage; 